import { Relationship } from '@celito.clients/types';
import { errorToast, successToast } from '@celito.clients/utils';
import { ObjectAttributeDefinitionSchema } from 'apps/web-client/src/types/object-attribute-definition';
import { ObjectDefinitionDto } from 'apps/web-client/src/types/object-definition-dto';

/**
 * Name of the synthetic object definition that carries the fields shared by
 * every object (system fields such as created_at_utc__s and the task
 * completion merge fields). Fetching `useGetObjectData(ALL_OBJECTS_DEFINITION_NAME)`
 * hits `metadata/object-definition/objects/allObjects`.
 */
export const ALL_OBJECTS_DEFINITION_NAME = 'allObjects';

/**
 * Union the per-object attribute definitions with the shared "all objects"
 * definitions, de-duplicated by attribute name (the per-object entry wins so
 * object-specific overrides are preserved). Returns the original object with an
 * augmented `objectAttributeDefinitions` list; the rest of the definition
 * (label, action definitions, etc.) is left untouched.
 */
export const mergeAllObjectFields = (
  object?: ObjectDefinitionDto,
  allObjectsDefinition?: ObjectDefinitionDto
): ObjectDefinitionDto | undefined => {
  if (!object) return object;

  const perObjectFields = object.objectAttributeDefinitions ?? [];
  const existingNames = new Set(perObjectFields.map(({ name }) => name));

  const extraFields = (
    allObjectsDefinition?.objectAttributeDefinitions ?? []
  ).filter(
    ({ name }: ObjectAttributeDefinitionSchema) => !existingNames.has(name)
  );

  if (!extraFields.length) return object;

  return {
    ...object,
    objectAttributeDefinitions: [...perObjectFields, ...extraFields],
  };
};

export const copyToClipboard = (infoToCopy: string) => {
  navigator.clipboard
    .writeText(infoToCopy)
    .then(() =>
      successToast({
        message: `Successfully copied ${infoToCopy} to clipboard`,
      })
    )
    .catch((error) =>
      errorToast({ message: 'Failure copying merge field, try again!' })
    );
};

export const generateMergeFieldName = (
  field: string,
  relationship?: Relationship,
  appendInitalObject?: boolean,
  objectName?: string,
  firstLevel?: string
) => {
  const mergeField = creatingMergeFieldName(
    field,
    relationship,
    appendInitalObject,
    objectName
  );

  if (firstLevel) {
    return `\${${firstLevel}.${mergeField}}`;
  }

  return `\${${mergeField}}`;
};

export const creatingMergeFieldName = (
  field?: string,
  relationship?: Relationship,
  appendInitalObject?: boolean,
  objectName?: string
) => {
  const appendedObject = appendInitalObject ? `${objectName}.` : '';

  const appendedField = field ? `.${field}` : '';

  let mergeField = `${appendedObject}${field}`;

  if (relationship) mergeField = `${relationship.name}${appendedField}`;

  return mergeField;
};
